import React from 'react';
import { TeCallout, TeContainer, TeText } from '@topcon/tuicl-react';
import { useTranslation } from '@topcon/topcon-utils';

import type { ErrorExplainerContent } from '../../utils/problemDetails.types';

interface ErrorExplanationBodyProps {
  readonly content: ErrorExplainerContent;
  readonly descriptionLabel?: string;
}

/**
 * Shared explanation body: an optional label above the description, the
 * description rendered in an info callout, and a "steps to resolve" list.
 * Reused by the error drawer and the `/error/:code` landing page so both
 * surfaces render identical content. The caller resolves and passes the
 * content so it is not resolved twice on the same render.
 * @param props - The resolved content and an optional label shown above the description.
 * @returns The shared explanation body.
 */
function ErrorExplanationBody({
  content,
  descriptionLabel,
}: ErrorExplanationBodyProps): React.JSX.Element {
  const { t } = useTranslation('errors');

  return (
    <TeContainer column gap="m">
      {descriptionLabel !== undefined && (
        <TeText size={16} weight={700}>
          {descriptionLabel}
        </TeText>
      )}

      <TeCallout status="info" text={content.description} grow />

      {content.steps.length > 0 && (
        <TeContainer column gap="xs">
          <TeText size={16} weight={700}>
            {t('stepsTitle')}
          </TeText>
          <TeText size={14} weight={400} color="text.secondary">
            {t('stepsIntro')}
          </TeText>
          <ul>
            {content.steps.map((step) => (
              <li key={step}>
                <TeText size={14} weight={400}>
                  {step}
                </TeText>
              </li>
            ))}
          </ul>
        </TeContainer>
      )}
    </TeContainer>
  );
}

export default ErrorExplanationBody;
